import { useTranslation } from 'react-i18next'
import { Utensils, Apple, Droplets, Wheat } from 'lucide-react'


export function MediterraneanDietSection() {
  const { t } = useTranslation('common')

  const pillars = [
    {
      icon: Wheat,
      key: 'grains',
    },
    {
      icon: Apple,
      key: 'vegetables',
    },
    {
      icon: Droplets,
      key: 'fats',
    },
    {
      icon: Utensils,
      key: 'proteins',
    },
  ]

  const benefits = [
    'heartHealth',
    'balancedNutrition',
    'lifestyle',
    'tradition',
  ]

  return (
    <section className="py-16 md:py-24 bg-white relative">
      <div className="container-fiorucci">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#00441C]/10 text-[#00441C] text-sm font-medium rounded-full mb-4">
            {t('nutrition.mediterranean.badge')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E2118] mb-6">
            {t('nutrition.mediterranean.title')}
          </h2>
          <p className="text-lg text-[#4E2118]/70 leading-relaxed">
            {t('nutrition.mediterranean.intro')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          {/* Left: Description */}
          <div className="space-y-6">
            <p className="text-[#4E2118]/80 leading-relaxed">
              {t('nutrition.mediterranean.paragraph1')}
            </p>
            <p className="text-[#4E2118]/80 leading-relaxed">
              {t('nutrition.mediterranean.paragraph2')}
            </p>
            <p className="text-[#4E2118]/80 leading-relaxed">
              {t('nutrition.mediterranean.paragraph3')}
            </p>

            {/* Benefits List */}
            <div className="pt-4">
              <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-4">
                {t('nutrition.mediterranean.benefitsTitle')}
              </h3>
              <ul className="space-y-3">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#DF631E]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-[#DF631E]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[#4E2118]/80">{t(`nutrition.mediterranean.benefits.${benefit}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Visual - Mediterranean Plate */}
          <div className="relative">
            {/* Central Circle */}
            <div className="relative w-full aspect-square max-w-md mx-auto">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#4E2118]/10" />

              {/* Center - Main message */}
              <div className="absolute inset-[25%] rounded-full bg-gradient-to-br from-[#DF631E] to-[#B85018] flex items-center justify-center shadow-xl">
                <div className="text-center text-white p-4">
                  <span className="block text-3xl font-serif font-bold">{t('nutrition.mediterranean.center.number')}</span>
                  <span className="block text-sm font-medium opacity-90">{t('nutrition.mediterranean.center.label')}</span>
                </div>
              </div>

              {/* Pillar items positioned around */}
              {pillars.map((pillar, index) => {
                const angle = (index * 90 - 45) * (Math.PI / 180)
                const radius = 42 // percentage
                const x = 50 + radius * Math.cos(angle)
                const y = 50 + radius * Math.sin(angle)

                return (
                  <div
                    key={pillar.key}
                    className="absolute w-20 h-20 -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="w-full h-full bg-white rounded-2xl shadow-lg border border-[#4E2118]/5 flex flex-col items-center justify-center p-2 hover:shadow-xl hover:scale-105 transition-all duration-300">
                      <pillar.icon className="w-6 h-6 text-[#DF631E] mb-1" />
                      <span className="text-[10px] font-medium text-[#4E2118] text-center leading-tight">
                        {t(`nutrition.mediterranean.pillars.${pillar.key}`)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Quote */}
        <div className="max-w-3xl mx-auto">
          <blockquote className="relative p-8 md:p-10 bg-[#FDF8F3] rounded-2xl border-l-4 border-[#DF631E]">
            <svg className="absolute top-6 left-6 w-10 h-10 text-[#DF631E]/20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
            <p className="relative text-lg md:text-xl text-[#4E2118] font-medium italic leading-relaxed pl-8">
              {t('nutrition.mediterranean.quote')}
            </p>
            <footer className="mt-4 pl-8">
              <cite className="text-sm text-[#4E2118]/60 not-italic">
                {t('nutrition.mediterranean.quoteAuthor')}
              </cite>
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  )
}
